import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { dmYhm } from "@/lib/dateFmt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Buildings, Plus, UserPlus, CheckCircle, ArrowClockwise, LockKey, EnvelopeSimple, PaperPlaneTilt, Trash, ArrowCounterClockwise, House, Warning, WhatsappLogo, Copy, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import WelcomeOverlay from "@/components/WelcomeOverlay";

/**
 * v1.25.0 — Super User Company Management
 * v1.25.10 — Send / resend login invites via email
 * v1.25.19 — Delete company with recoverable backup + Home button
 */
export default function SuperCompanies() {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [busy, setBusy] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [adminFor, setAdminFor] = useState(null);
    const [newName, setNewName] = useState("");
    const [newAdminEmail, setNewAdminEmail] = useState("");
    const [newAdminName, setNewAdminName] = useState("");
    const [newAdminPassword, setNewAdminPassword] = useState("");
    const [sendInvite, setSendInvite] = useState(true);
    // Resend / reset password dialog
    const [resendTarget, setResendTarget] = useState(null);
    const [resendNewPw, setResendNewPw] = useState("");
    const [resendBusy, setResendBusy] = useState(false);
    // v1.25.19 — Delete with backup
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState("");
    const [deleteReason, setDeleteReason] = useState("");
    const [deleteBusy, setDeleteBusy] = useState(false);
    // v1.25.19 — Backup list
    const [backups, setBackups] = useState([]);
    const [restoreBusy, setRestoreBusy] = useState("");
    // v1.25.20 — Share credentials (email + WhatsApp + copy)
    const [shareInfo, setShareInfo] = useState(null);   // { company_name, admin_email, password, admin_id, company_id, phone }
    const [sharePhone, setSharePhone] = useState("");
    // v1.25.48 — Welcome overlay after fresh company creation
    const [welcomeCompany, setWelcomeCompany] = useState(null);

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/super/companies");
            setCompanies(data.companies || []);
            const bResp = await api.get("/super/company-backups").catch(() => ({ data: { backups: [] } }));
            setBackups(bResp.data.backups || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load");
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => { load(); }, []);

    const createCompany = async () => {
        if (!newName.trim() || !newAdminEmail.trim() || !newAdminPassword) {
            toast.error("Fill company name, admin email, and password");
            return;
        }
        try {
            const { data: company } = await api.post("/super/companies", { name: newName.trim() });
            const { data: resp } = await api.post(`/super/companies/${company.id}/admins`, {
                email: newAdminEmail.trim(),
                name: newAdminName.trim() || newAdminEmail.split("@")[0],
                password: newAdminPassword,
                make_super_admin: true,
                send_invite: sendInvite,
            });
            if (sendInvite) {
                if (resp.invite_sent) toast.success(`Company "${company.name}" created · login sent to ${newAdminEmail}`);
                else toast.warning(`Company created but email failed: ${resp.invite_error || "unknown"}`);
            } else {
                toast.success(`Company "${company.name}" created with admin ${newAdminEmail}`);
            }
            // v1.25.20 — Open Share panel with the fresh credentials so the SU can
            // instantly forward them over WhatsApp or copy them to the clipboard.
            setShareInfo({
                company_name: company.name,
                company_id: company.id,
                admin_email: newAdminEmail.trim(),
                admin_name: newAdminName.trim() || newAdminEmail.split("@")[0],
                admin_id: resp.admin?.id,
                password: newAdminPassword,
            });
            setSharePhone("");
            setCreateOpen(false);
            setWelcomeCompany(company); // v1.25.48 — celebration overlay
            setNewName(""); setNewAdminEmail(""); setNewAdminName(""); setNewAdminPassword(""); setSendInvite(true);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        }
    };

    const toggleActive = async (c) => {
        if (!window.confirm(`${c.active ? "Deactivate" : "Reactivate"} company "${c.name}"?\n\nDeactivating stops all logins for this company but doesn't delete data.`)) return;
        try {
            await api.patch(`/super/companies/${c.id}`, { active: !c.active });
            toast.success(`Company ${c.active ? "deactivated" : "reactivated"}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    };

    const addAnotherAdmin = async () => {
        if (!newAdminEmail.trim() || !newAdminPassword) { toast.error("Fill email + password"); return; }
        try {
            const { data: resp } = await api.post(`/super/companies/${adminFor.id}/admins`, {
                email: newAdminEmail.trim(),
                name: newAdminName.trim() || newAdminEmail.split("@")[0],
                password: newAdminPassword,
                make_super_admin: false,
                send_invite: sendInvite,
            });
            if (sendInvite) {
                if (resp.invite_sent) toast.success(`Admin ${newAdminEmail} added · login sent`);
                else toast.warning(`Admin added but email failed: ${resp.invite_error || "unknown"}`);
            } else {
                toast.success(`Admin ${newAdminEmail} added to ${adminFor.name}`);
            }
            // v1.25.20 — Same Share panel after adding an additional admin.
            setShareInfo({
                company_name: adminFor.name,
                company_id: adminFor.id,
                admin_email: newAdminEmail.trim(),
                admin_name: newAdminName.trim() || newAdminEmail.split("@")[0],
                admin_id: resp.admin?.id,
                password: newAdminPassword,
            });
            setSharePhone("");
            setAdminFor(null);
            setNewAdminEmail(""); setNewAdminName(""); setNewAdminPassword(""); setSendInvite(true);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    };

    const openResend = (company, admin) => {
        setResendTarget({ company, admin });
        setResendNewPw("");
    };

    const sendResendLogin = async (resetPw) => {
        if (!resendTarget) return;
        if (resetPw && (!resendNewPw || resendNewPw.length < 6)) {
            toast.error("New password must be at least 6 characters");
            return;
        }
        setResendBusy(true);
        try {
            const payload = resetPw ? { new_password: resendNewPw } : {};
            await api.post(
                `/super/companies/${resendTarget.company.id}/admins/${resendTarget.admin.id}/resend-login`,
                payload,
            );
            toast.success(resetPw
                ? `Password reset & login sent to ${resendTarget.admin.email}`
                : `Login link resent to ${resendTarget.admin.email}`);
            setResendTarget(null);
            setResendNewPw("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Resend failed");
        } finally {
            setResendBusy(false);
        }
    };

    const executeDelete = async () => {
        if (!deleteTarget) return;
        // v1.27.85 — Confirmation must exactly match the company name.
        if (deleteConfirm.trim().toLowerCase() !== (deleteTarget.name || "").trim().toLowerCase()) {
            toast.error(`Type the exact company name "${deleteTarget.name}" to confirm`);
            return;
        }
        setDeleteBusy(true);
        try {
            const { data } = await api.post(`/super/companies/${deleteTarget.id}/delete-with-backup`, {
                confirm: deleteTarget.name,
                reason: deleteReason,
            });
            const recips = data?.final_summary?.recipients || [];
            toast.success(
                `Company deleted. Final ledger has been sent to your email.` +
                (recips.length ? ` (${recips.join(", ")})` : "")
            );
            setDeleteTarget(null);
            setDeleteConfirm("");
            setDeleteReason("");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        } finally {
            setDeleteBusy(false);
        }
    };

    const restoreBackup = async (b) => {
        if (!window.confirm(`Restore ${b.company_name} from backup?\n\nThis will re-create the company and all its ${b.total_rows} rows exactly as they were on ${dmYhm(b.deleted_at)}.`)) return;
        setRestoreBusy(b.id);
        try {
            const { data } = await api.post(`/super/company-backups/${b.id}/restore`);
            toast.success(`Restored ${b.company_name} · ${Object.values(data.restored_counts || {}).reduce((a, n) => a + n, 0)} rows recovered`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Restore failed");
        } finally {
            setRestoreBusy("");
        }
    };

    const purgeBackup = async (b) => {
        if (!window.confirm(`Permanently discard the backup for ${b.company_name}?\n\nThis cannot be undone. Only do this when you are 100% sure the data is no longer needed.`)) return;
        try {
            await api.delete(`/super/company-backups/${b.id}`);
            toast.success("Backup discarded");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    // v1.25.20 — Share credentials helpers
    const shareLoginUrl = (typeof window !== "undefined" ? `${window.location.origin}/login` : "https://teleadmin.in/login");
    const shareMessage = shareInfo ? (
        `Hi ${shareInfo.admin_name},\n\n` +
        `Your MICM admin account for *${shareInfo.company_name}* is ready.\n\n` +
        `Login URL: ${shareLoginUrl}\n` +
        `Email: ${shareInfo.admin_email}\n` +
        `Temporary Password: ${shareInfo.password}\n\n` +
        `Please sign in and change your password from the top-right user menu on first login.`
    ) : "";
    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(shareMessage);
            toast.success("Login details copied · paste anywhere");
        } catch { toast.error("Copy failed — please select the text manually"); }
    };
    const openWhatsApp = () => {
        const phone = sharePhone.replace(/\D/g, "");
        if (phone && phone.length < 10) { toast.error("Enter a 10-digit mobile number"); return; }
        const target = phone ? `https://wa.me/${phone.startsWith("91") || phone.length === 10 ? (phone.length === 10 ? "91" + phone : phone) : phone}` : "https://wa.me/";
        const url = `${target}?text=${encodeURIComponent(shareMessage)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };
    const resendEmail = async () => {
        if (!shareInfo?.admin_id || !shareInfo?.company_id) return;
        try {
            await api.post(`/super/companies/${shareInfo.company_id}/admins/${shareInfo.admin_id}/resend-login`, {});
            toast.success(`Login link emailed to ${shareInfo.admin_email}`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
    };

    return (
        <div className="space-y-6" data-testid="super-companies-page">
                <WelcomeOverlay
                    open={!!welcomeCompany}
                    onClose={() => setWelcomeCompany(null)}
                    title="Company Onboarded!"
                    subtitle={welcomeCompany ? `${welcomeCompany.name} is live. Share their admin credentials to get them started.` : ""}
                />
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Super User</div>
                        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900">Company Management</h1>
                        <p className="mt-2 text-sm text-slate-500">
                            Each company is a fully isolated tenant. Its admins and users see only their own parties, ledgers, uploads, and reports.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            data-testid="super-home"
                            variant="outline"
                            onClick={() => navigate("/admin/super/dashboard")}
                            className="border-slate-300"
                        >
                            <House size={14} className="mr-1" /> Home
                        </Button>
                        <Button data-testid="super-refresh" variant="outline" onClick={load} disabled={busy} className="border-slate-300">
                            <ArrowClockwise size={14} className="mr-1" /> Refresh
                        </Button>
                        <Button
                            data-testid="open-transfer-party"
                            variant="outline"
                            onClick={() => navigate("/admin/super/transfer-party")}
                            className="border-amber-300 text-amber-800 hover:bg-amber-50"
                        >
                            <UserPlus size={14} className="mr-1" /> Transfer Party
                        </Button>
                        <Button data-testid="super-new-company" onClick={() => setCreateOpen(true)} className="bg-violet-700 text-white hover:bg-violet-800">
                            <Plus size={14} className="mr-1.5" /> New Company
                        </Button>
                    </div>
                </div>

            {companies.map((c) => (
                <div key={c.id} data-testid={`company-card-${c.id}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
                        <div className="flex items-center gap-3">
                            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                                <Buildings size={20} weight="duotone" />
                            </div>
                            <div>
                                <div className="font-display text-lg font-bold text-slate-900">{c.name}</div>
                                <div className="text-[11px] text-slate-500">
                                    Code {c.code || "—"} · {c.active ? <span className="font-semibold text-emerald-700">ACTIVE</span> : <span className="font-semibold text-rose-700">INACTIVE</span>} · id <span className="font-mono">{c.id.slice(0, 8)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button data-testid={`add-admin-${c.id}`} variant="outline" size="sm" onClick={() => setAdminFor(c)} className="h-8 border-blue-300 text-xs text-blue-700 hover:bg-blue-50">
                                <UserPlus size={12} className="mr-1" /> Add Admin
                            </Button>
                            <Button data-testid={`toggle-active-${c.id}`} variant="outline" size="sm" onClick={() => toggleActive(c)} className={`h-8 text-xs ${c.active ? "border-rose-300 text-rose-700 hover:bg-rose-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
                                {c.active ? "Deactivate" : "Reactivate"}
                            </Button>
                            <Button
                                data-testid={`delete-company-${c.id}`}
                                variant="outline"
                                size="sm"
                                onClick={() => { setDeleteTarget(c); setDeleteConfirm(""); setDeleteReason(""); }}
                                className="h-8 border-rose-500 bg-rose-600 text-xs text-white hover:bg-rose-700"
                                title="Delete this company (a recoverable backup is saved first)"
                            >
                                <Trash size={12} className="mr-1" /> Delete
                            </Button>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4">
                        <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Parties</div><div className="font-display text-xl font-bold tabular-nums text-slate-900">{c.parties_count}</div></div>
                        <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Uploads</div><div className="font-display text-xl font-bold tabular-nums text-slate-900">{c.uploads_count}</div></div>
                        <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Transactions</div><div className="font-display text-xl font-bold tabular-nums text-slate-900">{c.transactions_count}</div></div>
                        <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Users</div><div className="font-display text-xl font-bold tabular-nums text-slate-900">{c.users_count}</div></div>
                    </div>
                    {c.admins?.length > 0 && (
                        <div className="border-t border-slate-100 bg-slate-50/40 px-4 py-3">
                            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-600">Company Admins</div>
                            <div className="flex flex-wrap gap-2">
                                {c.admins.map((a) => (
                                    <div key={a.id} data-testid={`admin-chip-${a.id}`} className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white pl-3 pr-1 py-1 text-xs text-slate-700">
                                        <LockKey size={11} className={a.is_super_admin ? "text-violet-600" : "text-slate-400"} />
                                        <span className="font-medium">{a.email}</span>
                                        {a.is_super_admin && <span className="rounded bg-violet-100 px-1 text-[9px] font-bold uppercase tracking-wider text-violet-700">Super Admin</span>}
                                        {a.is_super_user && <span className="rounded bg-amber-100 px-1 text-[9px] font-bold uppercase tracking-wider text-amber-700">Super User</span>}
                                        {!a.is_super_user && (
                                            <button
                                                type="button"
                                                data-testid={`resend-login-${a.id}`}
                                                onClick={() => openResend(c, a)}
                                                title="Send login details by email"
                                                className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 hover:bg-emerald-100"
                                            >
                                                <EnvelopeSimple size={11} weight="fill" /> Send Login
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ))}

            {/* Create Company + first Admin */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>New Company</DialogTitle>
                        <DialogDescription>
                            Creates an empty company (no parties, ledgers or uploads) and its first Company Admin. The admin will only see this company's data.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Company Name *</Label>
                            <Input data-testid="new-company-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. ABC Traders" className="mt-1" />
                        </div>
                        <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
                            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-violet-800">First Company Admin</div>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <div>
                                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Admin Email *</Label>
                                    <Input data-testid="new-admin-email" type="email" value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)} placeholder="admin@abctraders.in" className="mt-1 bg-white" />
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Admin Name</Label>
                                    <Input data-testid="new-admin-name" value={newAdminName} onChange={(e) => setNewAdminName(e.target.value)} placeholder="Admin display name" className="mt-1 bg-white" />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Temporary Password *</Label>
                                    <Input data-testid="new-admin-password" type="password" value={newAdminPassword} onChange={(e) => setNewAdminPassword(e.target.value)} placeholder="Min 6 chars" className="mt-1 bg-white" />
                                </div>
                            </div>
                            <label className="mt-3 flex items-center gap-2 text-xs font-medium text-slate-700">
                                <input
                                    type="checkbox"
                                    data-testid="send-invite-checkbox"
                                    checked={sendInvite}
                                    onChange={(e) => setSendInvite(e.target.checked)}
                                    className="h-4 w-4 rounded border-slate-300 accent-violet-600"
                                />
                                <EnvelopeSimple size={13} className="text-violet-700" />
                                <span>Email the login details to this admin right now</span>
                            </label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                        <Button data-testid="create-company-btn" onClick={createCompany} className="bg-violet-700 text-white hover:bg-violet-800">
                            <CheckCircle size={14} className="mr-1.5" /> Create Company + Admin
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add additional admin to an existing company */}
            <Dialog open={!!adminFor} onOpenChange={(v) => !v && setAdminFor(null)}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add Admin to {adminFor?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-slate-500">Email *</Label>
                            <Input data-testid="add-admin-email" type="email" value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)} className="mt-1" placeholder="second.admin@..." />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
                            <Input data-testid="add-admin-name" value={newAdminName} onChange={(e) => setNewAdminName(e.target.value)} className="mt-1" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-slate-500">Temporary Password *</Label>
                            <Input data-testid="add-admin-password" type="password" value={newAdminPassword} onChange={(e) => setNewAdminPassword(e.target.value)} className="mt-1" placeholder="Min 6 chars" />
                        </div>
                        <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                            <input
                                type="checkbox"
                                data-testid="add-admin-send-invite"
                                checked={sendInvite}
                                onChange={(e) => setSendInvite(e.target.checked)}
                                className="h-4 w-4 rounded border-slate-300 accent-green-600"
                            />
                            <EnvelopeSimple size={13} className="text-blue-700" />
                            <span>Email the login details to this admin right now</span>
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAdminFor(null)}>Cancel</Button>
                        <Button data-testid="save-add-admin" onClick={addAnotherAdmin} className="bg-blue-700 text-white hover:bg-blue-800">Add Admin</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Resend login / reset password */}
            <Dialog open={!!resendTarget} onOpenChange={(v) => !v && setResendTarget(null)}>
                <DialogContent data-testid="resend-login-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <PaperPlaneTilt size={18} weight="fill" className="text-emerald-700" /> Send Login Link
                        </DialogTitle>
                        <DialogDescription>
                            To <span className="font-semibold">{resendTarget?.admin?.email}</span> @ <span className="font-semibold">{resendTarget?.company?.name}</span>.
                            You can either resend the login URL only (keeps the current password), or reset the password to a new one and email both.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-slate-500">Optional — New temporary password</Label>
                            <Input
                                data-testid="resend-new-password"
                                type="text"
                                value={resendNewPw}
                                onChange={(e) => setResendNewPw(e.target.value)}
                                placeholder="Leave blank to keep the existing password"
                                className="mt-1"
                            />
                            <div className="mt-1 text-[10px] text-slate-500">If filled, the admin's password will be reset before the email goes out.</div>
                        </div>
                    </div>
                    <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button variant="outline" onClick={() => setResendTarget(null)}>Cancel</Button>
                        <Button
                            data-testid="resend-login-only-btn"
                            onClick={() => sendResendLogin(false)}
                            disabled={resendBusy}
                            className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                            <EnvelopeSimple size={14} className="mr-1.5" /> {resendBusy ? "Sending…" : "Resend Login Link"}
                        </Button>
                        <Button
                            data-testid="resend-login-reset-btn"
                            onClick={() => sendResendLogin(true)}
                            disabled={resendBusy || !resendNewPw || resendNewPw.length < 6}
                            className="bg-rose-600 text-white hover:bg-rose-700"
                        >
                            <LockKey size={14} className="mr-1.5" /> Reset Password & Send
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* v1.25.20 — Share Credentials (after create / add-admin) */}
            <Dialog open={!!shareInfo} onOpenChange={(v) => !v && setShareInfo(null)}>
                <DialogContent data-testid="share-credentials-dialog" className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-emerald-800">
                            <ShareNetwork size={18} weight="fill" /> Share Login Details
                        </DialogTitle>
                        <DialogDescription>
                            <b>{shareInfo?.admin_email}</b> can sign in to <b>{shareInfo?.company_name}</b> with the credentials below. Send them the info the way that's easiest for them.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <pre data-testid="share-message" className="max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-[11px] font-mono text-slate-800 whitespace-pre-wrap">{shareMessage}</pre>
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Admin's WhatsApp Mobile (optional, for WhatsApp)</Label>
                            <Input
                                data-testid="share-phone"
                                value={sharePhone}
                                onChange={(e) => setSharePhone(e.target.value)}
                                placeholder="10-digit Indian mobile (e.g. 9876543210)"
                                className="mt-1"
                                inputMode="tel"
                            />
                            <div className="mt-1 text-[10px] text-slate-500">Leave blank to open WhatsApp Web without a pre-selected contact.</div>
                        </div>
                    </div>
                    <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button variant="outline" onClick={() => setShareInfo(null)} className="border-slate-300">Close</Button>
                        <Button
                            data-testid="share-copy-btn"
                            variant="outline"
                            onClick={copyToClipboard}
                            className="border-slate-400 bg-slate-50 text-slate-800 hover:bg-slate-100"
                        >
                            <Copy size={14} className="mr-1.5" /> Copy details
                        </Button>
                        <Button
                            data-testid="share-email-btn"
                            variant="outline"
                            onClick={resendEmail}
                            className="border-blue-400 text-blue-800 hover:bg-blue-50"
                        >
                            <EnvelopeSimple size={14} className="mr-1.5" /> Email again
                        </Button>
                        <Button
                            data-testid="share-whatsapp-btn"
                            onClick={openWhatsApp}
                            className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                            <WhatsappLogo size={14} weight="fill" className="mr-1.5" /> Send on WhatsApp
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* v1.27.85 — Delete Company (pre-deletion final ledger email) */}
            <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
                <DialogContent data-testid="delete-company-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-rose-800">
                            <Warning size={18} weight="fill" /> Delete Company
                        </DialogTitle>
                        <DialogDescription>
                            <span className="block font-semibold text-rose-900">
                                This will permanently delete all data for this company.
                            </span>
                            <span className="mt-1 block">
                                A final account summary will be emailed before deletion. Type the company name to confirm.
                            </span>
                            <span className="mt-2 block text-[11px] text-slate-500">
                                Target: <b>{deleteTarget?.name}</b> · {deleteTarget?.parties_count} parties · {deleteTarget?.uploads_count} uploads · {deleteTarget?.transactions_count} txns · {deleteTarget?.users_count} users.
                                A recoverable backup is also saved so a Super User can restore this data if needed.
                            </span>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Reason (optional)</Label>
                            <Input data-testid="delete-reason" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} placeholder="Why is this company being deleted?" className="mt-1" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-slate-500">
                                Type <b className="font-mono text-rose-800">{deleteTarget?.name}</b> to confirm
                            </Label>
                            <Input
                                data-testid="delete-confirm"
                                value={deleteConfirm}
                                onChange={(e) => setDeleteConfirm(e.target.value)}
                                placeholder={deleteTarget?.name || ""}
                                className="mt-1 font-mono"
                                autoComplete="off"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                        <Button
                            data-testid="confirm-delete-company"
                            onClick={executeDelete}
                            disabled={
                                deleteBusy ||
                                deleteConfirm.trim().toLowerCase() !== (deleteTarget?.name || "").trim().toLowerCase()
                            }
                            className="bg-rose-700 text-white hover:bg-rose-800"
                        >
                            <Trash size={14} className="mr-1.5" />
                            {deleteBusy ? "Emailing summary + deleting…" : "Email Summary & Delete"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* v1.25.19 — Recoverable Backups list */}
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="backups-panel">
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="font-display text-lg font-bold text-slate-900">Recoverable Backups</h2>
                    <span className="text-[11px] text-slate-500">{backups.length} snapshot{backups.length === 1 ? "" : "s"} · never expire</span>
                </div>
                {backups.length === 0 && <div className="text-sm text-slate-500">No backups yet. Deleting a company automatically creates one.</div>}
                {backups.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="p-2">Company</th><th className="p-2">Deleted</th><th className="p-2 text-right">Rows</th><th className="p-2">By</th><th className="p-2">Status</th><th className="p-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {backups.map((b) => (
                                    <tr key={b.id} data-testid={`backup-row-${b.id}`}>
                                        <td className="p-2 font-semibold text-slate-900">{b.company_name}</td>
                                        <td className="p-2 whitespace-nowrap font-mono text-[11px]">{dmYhm(b.deleted_at)}</td>
                                        <td className="p-2 text-right font-mono tabular-nums">{b.total_rows}</td>
                                        <td className="p-2 text-slate-600">{b.deleted_by}</td>
                                        <td className="p-2">{b.restored_at ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-800">Restored</span> : <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-800">Recoverable</span>}</td>
                                        <td className="p-2">
                                            <div className="flex justify-end gap-1">
                                                {!b.restored_at && (
                                                    <Button
                                                        data-testid={`restore-backup-${b.id}`}
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => restoreBackup(b)}
                                                        disabled={restoreBusy === b.id}
                                                        className="h-7 border-emerald-300 text-xs text-emerald-800 hover:bg-emerald-50"
                                                    >
                                                        <ArrowCounterClockwise size={11} className="mr-1" /> {restoreBusy === b.id ? "Restoring…" : "Restore"}
                                                    </Button>
                                                )}
                                                <Button
                                                    data-testid={`purge-backup-${b.id}`}
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => purgeBackup(b)}
                                                    className="h-7 border-slate-300 text-xs text-slate-600 hover:bg-slate-50"
                                                >
                                                    <Trash size={11} />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
